/**
 * WASM-only ONNX session bootstrap (ticket 07, research ticket 01).
 *
 * The whole point of this package is to run UForm on `onnxruntime-web` (WASM)
 * — never `onnxruntime-node` (native, glibc-only). This module is the single
 * place that touches the runtime, so a scan of its imports proves the "no
 * native addon" constraint.
 *
 * The Emscripten `.wasm` runs in the JS engine's WebAssembly VM and never links
 * against the host libc, so the *same* artifact runs identically on glibc
 * (Debian `node:24-bookworm`) and musl (Alpine) images — in contrast to
 * `onnxruntime-node`, which ships a glibc-linked native prebuild with no musl
 * variant (onnxruntime issue #9483).
 */
import { env, InferenceSession, Tensor } from "onnxruntime-web";
export { env, InferenceSession, Tensor };
/**
 * Configure the WASM backend. Under Node, `onnxruntime-web` runs the Emscripten
 * WASM build single-threaded — it cannot spawn worker threads there (research
 * ticket 01). Force `numThreads = 1` so the build never attempts multi-threading
 * and falls back cleanly. No-op in the browser (threading is managed there).
 */
export function configureWasmRuntime() {
    if (typeof process !== "undefined" && process.versions?.node) {
        env.wasm.numThreads = 1;
    }
}
/**
 * Create an `InferenceSession` from a local file path (Node), a URL (browser),
 * or a byte buffer. `onnxruntime-web` reads a string path via `fs` under Node
 * and `fetch` in the browser (research ticket 01), so the same call works on
 * both sides.
 */
export async function createSession(model) {
    configureWasmRuntime();
    if (typeof model === "string") {
        return InferenceSession.create(model);
    }
    if (model instanceof Uint8Array) {
        return InferenceSession.create(model);
    }
    return InferenceSession.create(model);
}
/**
 * Copy the raw data of a float32 `embeddings` tensor into a plain `number[]`.
 * UForm returns a `[batch, 256]` tensor named `embeddings`; this flattens the
 * batch-1 case into the 256-dim vector every consumer expects.
 */
export function tensorToVector(tensor) {
    return Array.from(tensor.data);
}
/**
 * Pull the `embeddings` output out of a session run. UForm's text and image
 * encoders both emit their 256-dim projection under the name `embeddings`
 * (research ticket 02); fail loudly if a model ever renames it.
 */
export function embeddingsTensor(outputs) {
    const embeddings = outputs.embeddings;
    if (!embeddings) {
        throw new Error("UForm model output is missing the 'embeddings' tensor");
    }
    return embeddings;
}
//# sourceMappingURL=session.js.map